import React, { useState, useEffect, useMemo } from 'react';
import { LogOut, ChevronDown, ChevronUp, Check, FileDown } from 'lucide-react';
import { signOut } from '../data/auth';
import { LOGO_URL } from '../utils/assets';
import {
  meusServicos, meuWorkflow, etapasDoServico, andamento,
  resumoGeral, conteudoDaEtapa, filtrarPorPeriodo, PERIODOS,
  meuEstoque, resumoDoEstoque, embrioesPorCruzamento,
} from '../data/cliente';
import { gerarRelatorioPDF } from '../utils/relatorioPDF';
import { pdfsDaEtapa } from '../utils/clientePDF';
import { definirLaboratorioDoRelatorio } from '../utils/laboratorioNoRelatorio';
import { urlDaLogo } from '../data/configuracaoDoLaboratorio';

/**
 * Área do cliente: acompanhamento, somente leitura, feita para celular.
 *
 * Tela separada, não o app do laboratório com campos escondidos. Esconder é
 * frágil — basta um componente novo aparecer sem a checagem e o cliente vê o
 * que não deve. Aqui só existe o que ele pode ver, e os dados chegam por
 * funções que não devolvem custo, margem nem outro cliente.
 *
 * A leitura é por ETAPA. O cliente não pergunta "quais embriões existem";
 * pergunta o que saiu da OPU, o que saiu da FIV, quem recebeu, quantas
 * pegaram. Cada etapa abre e mostra o que ela produziu — e só abre a etapa que
 * o laboratório já fechou, regra que o banco aplica antes de mandar o dado.
 */

const COR = '#2a78d6';
const TRILHO = '#e6f0fd';

const data = (iso) => (iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : null);
const num = (n) => (Number(n) || 0).toLocaleString('pt-BR');
// Dose aceita meia palheta; o resto da tela conta coisa inteira. Vírgula, e
// sem zero sobrando: "2,5", não "2.50".
const doses = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0$/, '').replace('.', ',');
};

/* ------------------------------------------------------------------ */
/* Funil do topo                                                       */
/* ------------------------------------------------------------------ */

/**
 * Rótulo e número ficam ACIMA da barra, não ao lado.
 *
 * Ao lado, num celular de 360px, sobrariam uns 120px para a barra e a
 * proporção — que é a única coisa que a barra tem para dizer — viraria ruído.
 */
const Degrau = ({ degrau, topo, proximo }) => {
  const largura = topo > 0 ? Math.max((degrau.valor / topo) * 100, degrau.valor > 0 ? 2 : 0) : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-sm text-slate-600">{degrau.etapa}</span>
        <span className="text-base font-semibold text-slate-900 tabular-nums">{num(degrau.valor)}</span>
      </div>
      <div className="h-2.5 rounded-full" style={{ backgroundColor: TRILHO }}>
        <div className="h-full rounded-full" style={{ width: `${largura}%`, backgroundColor: COR }} />
      </div>

      {/* A conversão mora no vão entre as barras: é a perda de uma etapa para a
          seguinte, e é isso que o funil existe para mostrar. */}
      {proximo && (
        <p className="text-xs text-slate-500 py-2 pl-0.5">
          ↓ {proximo.taxa === null ? '—' : <><strong className="text-slate-700 tabular-nums">{proximo.taxa}%</strong> seguiram</>}
        </p>
      )}
    </div>
  );
};

/**
 * Uma pilha do estoque, com o total na tampa.
 *
 * O TOTAL FICA NA LINHA FECHADA, e é isso que faz o colapso valer aqui: fechada,
 * ela ainda responde "quantos" — que é a pergunta inteira para a maioria de quem
 * abre o portal. Só quem quer saber QUAIS precisa abrir.
 *
 * Sem isso o cliente com uma caneca cheia rolava trinta linhas de cruzamento para
 * chegar na dose de sêmen, que é o outro assunto e ficava no fim da tela.
 */
const PilhaDoEstoque = ({ titulo, total, unidade, aberta, onAlternar, children }) => (
  <div className="border border-slate-200 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onAlternar}
      aria-expanded={aberta}
      className="w-full min-h-[56px] px-4 flex items-center gap-3 text-left bg-white
                 hover:bg-slate-50 transition-colors"
    >
      <span className="text-2xl font-semibold text-slate-900 tabular-nums leading-none">
        {total}
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-slate-800">{titulo}</span>
        {unidade && <span className="block text-xs text-slate-500">{unidade}</span>}
      </span>
      {aberta
        ? <ChevronUp className="ml-auto w-5 h-5 text-slate-400 shrink-0" />
        : <ChevronDown className="ml-auto w-5 h-5 text-slate-400 shrink-0" />}
    </button>

    {aberta && (
      <div className="px-4 pb-3 border-t border-slate-100">
        {children}
      </div>
    )}
  </div>
);

/**
 * O que o cliente tem guardado no laboratório.
 *
 * Conteúdo da ABA "Estoque", e é por isso que ela existe: um embrião congelado
 * em março continua no botijão em setembro, e o serviço que o produziu já desceu
 * na lista — ou saiu do recorte de período. Amarrar o estoque ao período faria o
 * saldo mudar quando a pessoa trocasse o filtro, que é a coisa mais confusa que
 * um saldo pode fazer. Em aba, o seletor de período fica do outro lado, junto do
 * que ele governa.
 *
 * DUAS PILHAS COLAPSÁVEIS, e o padrão depende de quantas existem. Havendo as duas,
 * as duas começam fechadas: um cliente com a caneca cheia rolava trinta linhas de
 * cruzamento para chegar na dose de sêmen, que é o outro assunto. Havendo só uma,
 * ela abre — colapsar o único conteúdo da tela é pedir um toque para mostrar o que
 * a pessoa veio ver.
 *
 * DEVOLVE NADA QUANDO NÃO HÁ NADA, mesmo com a aba escondida nesse caso: a guarda
 * fica nos dois lugares porque um cartão dizendo "0 embriões, 0 doses" ocupa a
 * tela para não informar nada, e a tela não deveria depender de quem a chama para
 * isso.
 *
 * O endereço da palheta (botijão, caneca) não chega até aqui — nem no banco, nem
 * na tela. É o mapa interno do laboratório, muda quando alguém arruma o botijão, e
 * o cliente não tem o que fazer com ele.
 */
const Estoque = ({ estoque }) => {
  const r = resumoDoEstoque(estoque);
  const cruzamentos = useMemo(
    () => embrioesPorCruzamento(estoque?.embrioes), [estoque?.embrioes]);

  const temEmbrioes = r.embrioes > 0;
  const temSemen = r.partidas > 0;
  const soUma = temEmbrioes !== temSemen;

  // `null` significa "ninguém tocou ainda", e aí vale o padrão: aberta quando é a
  // única pilha, fechada quando as duas competem por espaço. Guardar o padrão em
  // estado desde o começo faria a pilha fechar sozinha quando o estoque mudasse.
  const [abertas, setAbertas] = useState({ embrioes: null, semen: null });
  const esta = (qual) => (abertas[qual] === null ? soUma : abertas[qual]);
  const alternar = (qual) => setAbertas(a => ({ ...a, [qual]: !esta(qual) }));

  if (r.vazio) return null;

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
      {/* SEM TÍTULO REPETINDO A ABA. A aba já diz "Estoque"; um rótulo logo
          abaixo dizendo a mesma coisa gasta a primeira linha da tela para não
          acrescentar nada. Fica a frase que EXPLICA, que é outra função. */}
      <p className="text-xs text-slate-500">
        O que está no botijão hoje. É um retrato, e não depende de período.
      </p>

      {temEmbrioes && (
        <PilhaDoEstoque
          titulo="Embriões congelados"
          total={num(r.embrioes)}
          unidade={`${cruzamentos.length} cruzamento${cruzamentos.length === 1 ? '' : 's'}`}
          aberta={esta('embrioes')}
          onAlternar={() => alternar('embrioes')}
        >
          <ul className="divide-y divide-slate-100">
            {cruzamentos.map(c => (
              <li key={`${c.doadora}-${c.touro}`} className="py-2 flex items-baseline gap-2">
                <span className="text-sm text-slate-800 min-w-0">
                  {c.doadora} <span className="text-slate-400">×</span> {c.touro}
                </span>
                {c.estagios.length > 0 && (
                  <span className="text-xs text-slate-400">{c.estagios.join(', ')}</span>
                )}
                <span className="ml-auto text-sm font-semibold text-slate-900 tabular-nums">
                  {num(c.quantidade)}
                </span>
              </li>
            ))}
          </ul>
        </PilhaDoEstoque>
      )}

      {temSemen && (
        <PilhaDoEstoque
          titulo="Doses de sêmen"
          total={doses(r.doses)}
          unidade={`${r.partidas} partida${r.partidas === 1 ? '' : 's'}`}
          aberta={esta('semen')}
          onAlternar={() => alternar('semen')}
        >
          <ul className="divide-y divide-slate-100">
            {(estoque?.semen || []).map(p => (
              <li key={`${p.touro}-${p.lote}`} className="py-2 flex items-baseline gap-2">
                <span className="text-sm text-slate-800 min-w-0">{p.touro || '—'}</span>
                {p.lote && <span className="text-xs text-slate-400">lote {p.lote}</span>}
                <span className="ml-auto text-sm font-semibold text-slate-900 tabular-nums">
                  {doses(p.doses)}
                </span>
              </li>
            ))}
          </ul>
        </PilhaDoEstoque>
      )}

      <p className="text-xs text-slate-400">
        Para retirar ou transferir, fale com o laboratório.
      </p>
    </section>
  );
};

const Resumo = ({ degraus }) => {
  if (!degraus.length) return null;
  const topo = Math.max(degraus[0].valor, 1);
  const fim = degraus[degraus.length - 1];

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5">
      {/* A figura principal é o fim do funil: é o resultado que se contrata. */}
      <p className="text-sm text-slate-500">{fim.etapa} até agora</p>
      <p className="text-4xl font-semibold text-slate-900 tabular-nums leading-tight mb-5">{num(fim.valor)}</p>

      {degraus.map((d, i) => (
        <Degrau key={d.etapa} degrau={d} topo={topo} proximo={degraus[i + 1]} />
      ))}

      <p className="text-xs text-slate-400 mt-3">Somando apenas as etapas já concluídas.</p>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Conteúdo de cada etapa                                              */
/* ------------------------------------------------------------------ */

/** Linha de duas alturas: quem é (forte) e quanto deu (à direita, alinhado). */
const Linha = ({ titulo, sub, valor, fraco }) => (
  <li className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-900 truncate">{titulo}</p>
      {sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}
    </div>
    <span className={`text-sm shrink-0 tabular-nums ${fraco ? 'text-slate-500' : 'text-slate-900 font-medium'}`}>
      {valor}
    </span>
  </li>
);

const DetalheOPU = ({ linhas }) => (
  <ul>
    {linhas.map((d, i) => (
      <Linha key={`${d.doadora}-${i}`}
        titulo={d.doadora || 'Doadora não identificada'}
        sub={d.touro ? `Touro ${d.touro}` : null}
        valor={`${num(d.aspirados)} asp · ${num(d.viaveis)} viáveis`} />
    ))}
  </ul>
);

const DetalheFIV = ({ linhas, embrioes }) => {
  // Embrião agrupado pela doadora que o produziu: é assim que o cliente pensa
  // o resultado ("o que a minha vaca X deu"), e não como uma lista corrida.
  const porDoadora = useMemo(() => {
    const m = new Map();
    embrioes.forEach(e => {
      const k = e.doadora || '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [embrioes]);

  return (
    <ul>
      {linhas.map((d, i) => {
        const seus = porDoadora.get(d.doadora) || [];
        return (
          <li key={`${d.doadora}-${i}`} className="py-2.5 border-b border-slate-100 last:border-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{d.doadora || 'Doadora não identificada'}</p>
                <p className="text-xs text-slate-500 truncate">
                  {d.touro ? `Touro ${d.touro}` : 'Touro não informado'}
                  {' · '}{num(d.oocitos)} oócitos · {num(d.clivados)} clivados
                </p>
              </div>
              <span className="text-sm font-medium text-slate-900 shrink-0 tabular-nums">
                {num(seus.length)} {seus.length === 1 ? 'embrião' : 'embriões'}
              </span>
            </div>

            {/* A fase é a informação por embrião que o cliente pede. Como
                etiqueta, cabe na largura do celular e se lê de relance. */}
            {seus.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {seus.map(e => (
                  <span key={e.numero}
                    className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700 tabular-nums">
                    #{e.numero ?? '—'}{e.estagio ? ` ${e.estagio}` : ''}
                  </span>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const DetalheTE = ({ linhas }) => (
  <ul>
    {linhas.map((t, i) => {
      const aprovada = (t.status || '').toUpperCase() === 'APROVADA';
      return (
        <Linha key={`${t.embriao}-${i}`}
          titulo={aprovada
            ? (t.receptora || 'Receptora não informada')
            : `Embrião #${t.embriao}`}
          sub={aprovada
            ? `Embrião #${t.embriao}${t.doadora ? ` · ${t.doadora}` : ''}${t.touro ? ` × ${t.touro}` : ''}`
            : [t.doadora, t.touro].filter(Boolean).join(' × ') || null}
          /* Não transferido é estado, não ausência: dizer o status por escrito
             evita a linha em branco que parece erro de cadastro. */
          valor={aprovada ? 'transferido' : (t.status || 'não transferido').toLowerCase()}
          fraco={!aprovada} />
      );
    })}
  </ul>
);

const DetalhePrenhez = ({ linhas }) => (
  <ul>
    {linhas.map((p, i) => {
      const prenhe = (p.resultado || '').toUpperCase().startsWith('PRENH');
      return (
        <Linha key={`${p.receptora}-${p.embriao}-${i}`}
          titulo={p.receptora || 'Receptora não informada'}
          sub={p.embriao ? `Embrião #${p.embriao}` : null}
          valor={(p.resultado || 'aguardando').toLowerCase()}
          fraco={!prenhe} />
      );
    })}
  </ul>
);

const CorpoDaEtapa = ({ conteudo }) => {
  if (!conteudo.linhas.length) {
    return <p className="text-sm text-slate-500 py-3">Sem detalhes lançados nesta etapa.</p>;
  }
  if (conteudo.tipo === 'opu') return <DetalheOPU linhas={conteudo.linhas} />;
  if (conteudo.tipo === 'fiv') return <DetalheFIV linhas={conteudo.linhas} embrioes={conteudo.embrioes} />;
  if (conteudo.tipo === 'te') return <DetalheTE linhas={conteudo.linhas} />;
  if (conteudo.tipo === 'prenhez') return <DetalhePrenhez linhas={conteudo.linhas} />;
  return null;
};

/* ------------------------------------------------------------------ */
/* Etapa                                                               */
/* ------------------------------------------------------------------ */

/**
 * Uma etapa da linha do tempo — e, quando concluída, a porta para o detalhe.
 *
 * O estado é dito por texto e por posição, não por cor: "Feito em 21/08" e
 * "Previsto para 28/08" já se distinguem lidos em voz alta. A cor só reforça.
 * Etapa pendente não é clicável: um botão que abre o nada ensina a não confiar
 * nos outros.
 */
const Etapa = ({ etapa, ultima, detalhe, aberta, onAbrir, servico, cadastro }) => {
  const conteudo = etapa.feito && detalhe ? conteudoDaEtapa(etapa.id, detalhe) : null;
  const abrivel = !!(etapa.feito && detalhe);
  const pdfs = aberta && conteudo
    ? pdfsDaEtapa({ conteudo, etapa, servico, cliente: cadastro?.cliente, pdf: detalhe?.pdf })
    : [];

  const cabecalho = (
    <div className="flex items-start gap-3 flex-1 min-w-0 text-left">
      <div className="flex flex-col items-center self-stretch">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5
                        ${etapa.feito ? 'bg-blue-600' : 'bg-white border-2 border-slate-200'}`}>
          {etapa.feito && <Check size={14} className="text-white" strokeWidth={3} />}
        </div>
        {!ultima && <div className={`w-px flex-1 mt-1 ${etapa.feito ? 'bg-blue-200' : 'bg-slate-200'}`} />}
      </div>

      <div className="min-w-0 flex-1 pb-1">
        <p className={`text-sm ${etapa.feito ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
          {etapa.rotulo}
        </p>
        <p className="text-xs text-slate-500">
          {etapa.feito
            ? `Feito em ${data(etapa.data) || '—'}`
            : etapa.previsto ? `Previsto para ${data(etapa.previsto)}` : 'A agendar'}
          {conteudo?.resumo && <span className="text-slate-600"> · {conteudo.resumo}</span>}
        </p>
      </div>
    </div>
  );

  return (
    <div>
      {abrivel ? (
        <button onClick={() => onAbrir(etapa.id)} aria-expanded={aberta}
          className="w-full flex items-start gap-2 min-h-[56px] py-2 -mx-2 px-2 rounded-lg hover:bg-slate-50 active:bg-slate-100">
          {cabecalho}
          <ChevronDown size={18}
            className={`text-slate-400 shrink-0 mt-1.5 transition-transform ${aberta ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <div className="flex items-start gap-2 min-h-[56px] py-2">{cabecalho}</div>
      )}

      {aberta && conteudo && (
        <div className="ml-9 mb-3 pl-3 border-l-2 border-blue-100">
          <CorpoDaEtapa conteudo={conteudo} />
          {/* Os mesmos PDFs que o laboratório tira desta etapa no desktop —
              a FIV tem dois, Clivagem e Embriões, como lá. */}
          {pdfs.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {pdfs.map(p => (
                <button key={p.rotulo} onClick={p.gerar}
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 active:bg-slate-100">
                  <FileDown size={16} /> {p.rotulo}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Serviço                                                             */
/* ------------------------------------------------------------------ */

const CartaoServico = ({ servico, aberto, onAbrir, detalhe, etapaAberta, onAbrirEtapa, cadastro }) => {
  const est = andamento(servico);
  const etapas = etapasDoServico(servico);
  const progresso = est.total > 0 ? (est.feitas / est.total) * 100 : 0;
  const concluido = est.rotulo === 'Concluído';

  return (
    <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button onClick={() => onAbrir(servico.service_id)} aria-expanded={aberto}
        className="w-full text-left p-4 min-h-[72px] hover:bg-slate-50 active:bg-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-slate-900">
              {servico.service_code ? `#${servico.service_code}` : 'Serviço'}
              <span className="text-slate-400 font-normal"> · {data(servico.service_date)}</span>
            </p>
            <p className="text-sm text-slate-500 mt-0.5">{est.rotulo}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs px-2 py-1 rounded-full tabular-nums
                             ${concluido ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
              {est.feitas}/{est.total}
            </span>
            <ChevronDown size={18} className={`text-slate-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* A barra repete o "3 de 5" em forma. Serve para varrer a lista sem
            ler: de relance dá para ver quais serviços estão perto do fim. */}
        <div className="h-1.5 rounded-full mt-3" style={{ backgroundColor: TRILHO }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${progresso}%`, backgroundColor: COR }} />
        </div>
      </button>

      {aberto && (
        <div className="px-4 pb-3 border-t border-slate-100 pt-2">
          {!detalhe ? (
            <p className="text-sm text-slate-500 py-4">Carregando...</p>
          ) : detalhe.erro ? (
            <p className="text-sm text-slate-600 py-4">{detalhe.erro}</p>
          ) : (
            etapas.map((e, i) => (
              <Etapa key={e.id} etapa={e} ultima={i === etapas.length - 1}
                detalhe={detalhe} aberta={etapaAberta === e.id} onAbrir={onAbrirEtapa}
                servico={servico} cadastro={cadastro} />
            ))
          )}
        </div>
      )}
    </article>
  );
};

/* ------------------------------------------------------------------ */
/* Tela                                                                */
/* ------------------------------------------------------------------ */

/**
 * As colunas do relatório do cliente.
 *
 * As mesmas quatro contagens do funil, por serviço. Nada aqui existe que a
 * tela já não mostre — o portal não recebe valor do servidor, e o papel não
 * inventa coluna que a tela não tem.
 */
const COLUNAS_CLIENTE = [
  { chave: 'cod',  rotulo: 'Serviço', valor: (s) => s.service_code || '—' },
  { chave: 'data', rotulo: 'Data',    valor: (s) => data(s.service_date) || '—' },
  { chave: 'sit',  rotulo: 'Situação', valor: (s) => andamento(s).rotulo },
  { chave: 'ooc',  rotulo: 'Oócitos',  numerica: true,
    valor: (s) => (s.opu_done ? num(s.oocitos) : '—') },
  { chave: 'emb',  rotulo: 'Embriões', numerica: true,
    valor: (s) => (s.fiv_done ? num(s.embrioes) : '—') },
  { chave: 'te',   rotulo: 'Transf.',  numerica: true,
    valor: (s) => (s.te_done ? num(s.transferidos) : '—') },
  // Travessão, e não zero: etapa que não aconteceu é diferente de etapa que
  // deu zero, e o zero faria o serviço em andamento parecer fracassado.
  { chave: 'pre',  rotulo: 'Prenhes',  numerica: true, forte: true,
    valor: (s) => (s.dg60_done ? num(s.prenhes) : '—') },
];

const FILTROS = [
  { id: 'todos',    rotulo: 'Todos' },
  { id: 'andamento', rotulo: 'Em andamento' },
  { id: 'concluidos', rotulo: 'Concluídos' },
];

const AreaCliente = ({ cadastro, sessao, onSair }) => {
  const [servicos, setServicos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [etapaAberta, setEtapaAberta] = useState(null);
  const [detalhes, setDetalhes] = useState({});
  const [filtro, setFiltro] = useState('todos');
  const [periodo, setPeriodo] = useState('tudo');
  const [estoque, setEstoque] = useState({ embrioes: [], semen: [] });
  /*
   * DUAS ABAS, e não duas seções empilhadas.
   *
   * O estoque era uma seção acima do seletor de período, e ficava ali por uma
   * razão defensiva: qualquer coisa desenhada ABAIXO daquele seletor se lê como
   * se ele a governasse, e um saldo que parece mudar de valor conforme o filtro
   * é a coisa mais confusa que um saldo pode fazer.
   *
   * Aba resolve isso melhor do que a ordem. São duas perguntas diferentes —
   * "como vão os meus serviços" e "o que eu tenho guardado" —, e cada uma com o
   * seu tempo: a primeira olha um período, a segunda é o retrato de hoje. Em aba,
   * o seletor de período fica dentro da aba que ele governa, e não sobra dúvida.
   *
   * O portal abre nos SERVIÇOS, que é o que traz a pessoa ao link.
   */
  const [aba, setAba] = useState('servicos');

  // Os PDFs daqui saem com a cara do laboratório do cliente, como os do
  // desktop. `laboratorio_dados` vem das duas portas (conta e link); sem ele
  // (banco antigo), fica o nome, e o relatório sai como sempre saiu.
  useEffect(() => {
    const dados = cadastro?.laboratorio_dados;
    definirLaboratorioDoRelatorio(dados
      ? { ...dados, logo_url: urlDaLogo(dados.logo_path) }
      : cadastro?.laboratorio ? { name: cadastro.laboratorio } : null);
  }, [cadastro]);

  useEffect(() => {
    let cancelado = false;
    meusServicos(sessao)
      .then(l => { if (!cancelado) setServicos(l || []); })
      .catch(e => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [sessao]);

  /**
   * O estoque em chamada própria, e que falha em silêncio.
   *
   * Separada dos serviços de propósito: o estoque é uma seção a mais, e o erro
   * dele não pode derrubar o acompanhamento — que é a razão de a pessoa ter
   * aberto o link. Sem estoque a seção some; com erro nos serviços, a tela
   * inteira precisa dizer.
   */
  useEffect(() => {
    let cancelado = false;
    meuEstoque(sessao)
      .then(e => { if (!cancelado) setEstoque(e); })
      .catch(e => console.error('Estoque do cliente:', e));
    return () => { cancelado = true; };
  }, [sessao]);

  // O recorte de período vem PRIMEIRO, e vale para o funil, para a lista e
  // para o PDF. Aplicá-lo só na lista deixaria o total do topo contando
  // serviços que a pessoa não está vendo.
  const doPeriodo = useMemo(() => filtrarPorPeriodo(servicos, periodo), [servicos, periodo]);

  // Decide qual "vazio" a tela mostra: sem serviço E sem estoque é tela vazia
  // de verdade; sem serviço mas com palheta guardada é só uma linha de aviso
  // embaixo do que a pessoa veio ver.
  const resumoEstoque = useMemo(() => resumoDoEstoque(estoque), [estoque]);
  const semEstoque = resumoEstoque.vazio;

  const degraus = useMemo(() => resumoGeral(doPeriodo), [doPeriodo]);

  const visiveis = useMemo(() => doPeriodo.filter(s => {
    if (filtro === 'todos') return true;
    const concluido = andamento(s).rotulo === 'Concluído';
    return filtro === 'concluidos' ? concluido : !concluido;
  }), [doPeriodo, filtro]);

  /**
   * O relatório do cliente, em papel.
   *
   * Usa o MESMO gerador dos relatórios do laboratório e os MESMOS números que
   * esta tela mostra — `degraus` é o funil que está logo acima, e `visiveis`
   * são os serviços listados abaixo.
   *
   * NÃO HÁ VALOR NENHUM AQUI, e isso não é esquecimento: o portal nunca
   * recebeu dinheiro do servidor. `servicos_por_token` devolve etapas e
   * contagens, e mais nada. Um relatório com preço exigiria abrir a RPC — e
   * essa é decisão comercial, não de tela.
   */
  const baixarRelatorio = () => {
    const rotuloPeriodo = PERIODOS.find(p => p.id === periodo)?.rotulo || 'Tudo';
    gerarRelatorioPDF({
      titulo: 'Acompanhamento',
      // `meuCadastro` devolve { cliente, laboratorio }. O laboratório assina o
      // documento; o cliente é de quem ele fala, e por isso sai no corpo.
      laboratorio: cadastro?.laboratorio || '',
      filtros: [
        ['Cliente', cadastro?.cliente || '—'],
        ['Período', rotuloPeriodo],
        ['Serviços', `${visiveis.length} de ${servicos.length}`],
      ],
      indicadores: degraus.map(d => ({
        rotulo: d.etapa,
        valor: num(d.valor),
        detalhe: d.taxa === null ? '' : `${d.taxa}% da etapa anterior`,
      })),
      detalhe: {
        titulo: 'Serviços',
        colunas: COLUNAS_CLIENTE,
        linhas: visiveis,
      },
    });
  };

  const abrir = async (servico) => {
    const id = servico.service_id;
    if (aberto === id) { setAberto(null); return; }
    setAberto(id);
    setEtapaAberta(null);

    // Carrega uma vez por serviço: o detalhe não muda enquanto a tela está
    // aberta, e recarregar a cada abertura gastaria rede à toa em campo.
    if (detalhes[id]) return;
    try {
      const d = await meuWorkflow(id, sessao);
      setDetalhes(prev => ({ ...prev, [id]: d || {} }));
    } catch (e) {
      setDetalhes(prev => ({ ...prev, [id]: { erro: e.message } }));
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL()} alt="" className="h-9 w-auto shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate leading-tight">{cadastro?.cliente}</p>
              <p className="text-xs text-slate-500 truncate">{cadastro?.laboratorio}</p>
            </div>
          </div>
          {/* Por link não há sessão de conta para encerrar — o que existe é o
              acesso lembrado neste aparelho, e é isso que o botão apaga. */}
          <button onClick={() => (onSair ? onSair() : signOut())} title="Sair"
            className="p-3 -mr-1 text-slate-400 hover:text-slate-700 rounded-lg shrink-0">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-16">
        {carregando && <p className="text-sm text-slate-500 py-10 text-center">Carregando...</p>}

        {erro && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
            Não foi possível carregar: {erro}
          </div>
        )}

        {/*
          * A ABINHA DO ESTOQUE só existe havendo estoque.
          *
          * Aba vazia é promessa não cumprida: quem clica em "Estoque" para ler
          * "nada aqui" gastou um toque para descobrir o que a ausência da aba já
          * dizia. E ela traz a contagem, porque é a resposta inteira para metade
          * dos clientes — dois deles têm palheta guardada e nenhum serviço
          * lançado, e abriam o link para ler "nenhum serviço registrado" sem
          * ficar sabendo da dose que está no botijão.
          */}
        {!carregando && !erro && !semEstoque && (
          <div className="flex gap-1 border-b border-slate-200 -mt-1">
            {[
              { id: 'servicos', rotulo: 'Serviços', contagem: servicos.length },
              {
                id: 'estoque',
                rotulo: 'Estoque',
                contagem: resumoEstoque.embrioes + resumoEstoque.partidas,
              },
            ].map(x => (
              <button
                key={x.id}
                onClick={() => setAba(x.id)}
                aria-pressed={aba === x.id}
                className={`px-4 min-h-[44px] text-sm border-b-2 -mb-px transition-colors ${
                  aba === x.id
                    ? 'border-blue-600 text-slate-900 font-medium'
                    : 'border-transparent text-slate-500'}`}
              >
                {x.rotulo}
                {x.contagem > 0 && (
                  <span className={`ml-1.5 tabular-nums text-xs ${
                    aba === x.id ? 'text-slate-500' : 'text-slate-400'}`}>
                    {x.contagem}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {!carregando && !erro && (aba === 'estoque' && !semEstoque) && (
          <Estoque estoque={estoque} />
        )}

        {!carregando && !erro && aba === 'servicos' && servicos.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl py-14 text-center text-slate-500">
            <p className="text-sm">Nenhum serviço registrado ainda.</p>
            {/* Com palheta no botijão, o vazio dos serviços aponta para a outra
                aba em vez de ser o fim da tela. */}
            {!semEstoque && (
              <button
                type="button"
                onClick={() => setAba('estoque')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Ver o que está guardado no laboratório
              </button>
            )}
          </div>
        )}

        {!carregando && !erro && aba === 'servicos' && servicos.length > 0 && (
          <>
            {/* Período e impressão ficam ACIMA do funil: os dois mexem no
                que o funil mostra, e um controle abaixo do número que ele
                altera se lê como se não tivesse relação.

                E ficam DENTRO da aba de serviços, que é a única que eles
                governam — o estoque é o retrato de hoje e não tem período. */}
            <div className="flex items-center gap-2">
              <select
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                className="flex-1 min-h-[44px] px-3 bg-white border border-slate-200 rounded-xl
                           text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PERIODOS.map(p => (
                  <option key={p.id} value={p.id}>{p.rotulo}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={baixarRelatorio}
                className="flex items-center gap-1.5 min-h-[44px] px-4 bg-white border border-slate-200
                           rounded-xl text-sm text-slate-700 hover:border-slate-300 transition-colors
                           whitespace-nowrap"
              >
                <FileDown className="w-4 h-4" /> Relatório
              </button>
            </div>

            <Resumo degraus={degraus} />

            <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
              {FILTROS.map(f => (
                <button key={f.id} onClick={() => setFiltro(f.id)}
                  className={`px-4 min-h-[40px] rounded-full text-sm whitespace-nowrap border transition-colors
                             ${filtro === f.id
                               ? 'bg-blue-600 border-blue-600 text-white font-medium'
                               : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {f.rotulo}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {visiveis.map(s => (
                <CartaoServico key={s.service_id} servico={s}
                  aberto={aberto === s.service_id} onAbrir={() => abrir(s)}
                  detalhe={detalhes[s.service_id]}
                  etapaAberta={etapaAberta}
                  cadastro={cadastro}
                  onAbrirEtapa={(id) => setEtapaAberta(a => (a === id ? null : id))} />
              ))}
              {visiveis.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-10">Nenhum serviço neste filtro.</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AreaCliente;
